import { Form, message } from "antd";
import AButton from "core/presentation/components/Button";
import ATable from "core/presentation/components/Tables";
import AText from "core/presentation/components/Text";
import ATitle from "core/presentation/components/Title";
import { useEffect, useState, useMemo, useCallback } from "react";
import { typography } from "theme/Typography";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "core/presentation/store/hooks";
import Loader from "core/presentation/components/Loader";
import { RootState } from "core/presentation/store";
import { useSelector } from "react-redux";
import SearchFilterSection from "core/presentation/components/SearchFilterSection";
import { fleetThunks } from "fleet/presentation/store/fleetSlice";
import DeleteConfirmationModal from "core/presentation/components/DeleteCofirmationBox";
import { userThunks } from "user/presentation/store/userSlice";
import PaginationTable from "core/presentation/components/PaginationTable";

const TripDetailsList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const { userProfile } = useAppSelector((state) => state.user);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<any>();
  const [tripList, setTripList] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [modalVisible, setModalVisible] = useState(false);

  const handleConfirm = (tripStatus: string) => {
    setModalVisible(false);
  };
  const handleChangeTripStatus = (tripNo: any) => {
    setModalVisible(true);
  };
  const { trips, isLoading, error, tripCount } = useSelector(
    (state: RootState) => state.fleet
  );

  const handleLocation = useCallback(
    async (lat: number, lng: number) => {
      if (lat === undefined || lng === undefined) {
        return "-";
      }
      try {
        const response = await userThunks.reverseGeoCode({
          latitude: lat,
          longitude: lng,
        });
        const { state, district, municipality } = response?.data?.data;
        return `${municipality}, ${district}, ${state}` || "-";
      } catch (error) {
        message.error("Failed to fetch location information");
        return `${lat}, ${lng}`;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (trips) {
        const updatedTrips = await Promise.all(
          trips?.map(async (trip: any) => {
            const from =
              trip?.locationFrom?.location ??
              (trip?.locationFrom?.lat && trip?.locationFrom?.lng
                ? await handleLocation(
                    trip?.locationFrom?.lat,
                    trip?.locationFrom?.lng
                  )
                : "-");
            const to =
              trip?.locationTo?.location ??
              (trip?.locationTo?.lat && trip?.locationTo?.lng
                ? await handleLocation(
                    trip?.locationTo?.lat,
                    trip?.locationTo?.lng
                  )
                : "-");
            return { ...trip, from, to };
          })
        );
        setTripList(updatedTrips);
      }
    };

    fetchData();
  }, [trips, handleLocation]);

  const getTrip = (page: number, input?: string, filterValue?: string) => {
    dispatch(
      fleetThunks.getAllTripThunk({
        page: page,
      })
    );
  };

  useEffect(() => {
    getTrip(1, "", "");
  }, [dispatch]);

  const renderLocation = (text: string) => {
    const locationParts = text.split(",");
    const firstLine = locationParts.slice(0, 2).join(", ");
    const secondLine = locationParts.slice(2).join(", ");
    return (
      <div>
        <div>{firstLine}</div>
        <div style={{ color: "gray", fontSize: "0.9em" }}>{secondLine}</div>
      </div>
    );
  };

  const haveAction = userProfile?.user?.permissions.includes("trip:detail");

  const columns = [
    { title: "Trip No.", dataIndex: "tripNo", key: "tripNo" },
    { title: "Vehicle No.", dataIndex: "vehicleNo", key: "vehicleNo" },
    { title: "From", dataIndex: "from", key: "from", render: renderLocation },
    { title: "To", dataIndex: "to", key: "to", render: renderLocation },
    { title: "Trip Status", dataIndex: "tripstatus", key: "tripstatus" },
    ...(haveAction
      ? [{ title: "Action", dataIndex: "action", key: "action" }]
      : []),
  ];

  const handleDelete = async (id: any) => {
    try {
      await dispatch(fleetThunks.deleteTripThunk(id));
      message.success("Trip deleted successfully");
      setDeleteModalVisible(false);
    } catch (error) {
      message.error("Trip delete failed");
    } finally {
      getTrip(currentPage);
    }
  };

  const handleCreateTrip = () => {
    navigate("/trip/create");
  };
  const showDeleteConfirm = (id: any) => {
    setSelectedUserId(id);
    setDeleteModalVisible(true);
  };

  const tripDataSource = useMemo(
    () =>
      tripList
        .map((data: any) => ({
          tripNo: data.tripNumber,
          vehicleNo: data?.vehicleNumber ?? "-",
          from: data.from,
          to: data.to,
          tripstatus: data.tripStatus || "-",
          action: (
            <>
              {userProfile?.user?.permissions.includes("trip:detail") && (
                <AButton
                  style={{
                    marginTop: "0",
                    padding: "5px",
                    color: "#F26533",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                  type="text"
                  onClick={() => navigate(`/trip/details/${data?.tripId}`)}
                >
                  View
                </AButton>
              )}
            </>
          ),
        })),
    [tripList, userProfile]
  );

  return (
    <div>
      {loading && <Loader />}
      {!loading && (
        <>
          <div>
            <ATitle content={"Trip Details"} fontSize={14} fontWeight={600} />
          </div>

          <div style={{ marginTop: "10px" }}>
            <PaginationTable
              dataSource={tripDataSource}
              columns={columns}
              tableTitle="Trip List"
              loading={loading || isLoading || !tripList}
              totalData={tripCount}
              handleChange={getTrip}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </>
      )}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={() => handleDelete(selectedUserId)}
        onCancel={() => setDeleteModalVisible(false)}
      />
    </div>
  );
};

export default TripDetailsList;
